import Graphic from '@arcgis/core/Graphic';
import MapView from '@arcgis/core/views/MapView';
import GraphicsLayer from '@arcgis/core/layers/GraphicsLayer';
import { MapPlugin } from '../arcgis-types';
import GraphicHighlighter, { Highlighter } from '../utils/graphic-highlighter';
import GraphicHitTester, { GraphicHitTest } from '../utils/graphic-hit-tester';
import { REPEATABLE_LAYER_ID } from './repeatables';
import { FULCRUM_DECK_LAYER_ID } from '../arc-gis-map-view';

let graphicHighlighter;

const changeCursorOnValidGraphic = async (mapView: MapView, graphic: Graphic | undefined) => {
  const deckLayer = mapView.map.findLayerById(FULCRUM_DECK_LAYER_ID);
  const defaultCursor = graphic?.attributes?.shape?.item?._id ? 'pointer' : 'default';
  // @ts-ignore
  deckLayer.deck.set('getCursor', ({ isDragging }) => (isDragging ? 'grabbing' : defaultCursor));
};

/**
 * The RepeatableInteractivity plugin enables interactivity between map graphics
 * and a data table by handling hover and click events. It highlights repeatable map graphics
 * and synchronizes these interactions with the corresponding table rows via
 * the parent component (<Editor>/<RepeatableMapView>/RepeatableInteractivity).
 */

const RepeatableInteractivity: (
  onFeatureHover: (feature: Record<string, any> | undefined) => void,
  onFeatureClick: (feature: Record<string, any> | undefined) => void,
  hoveredTableRowFeature: () => Record<string, any>,
  statusColor: string,
  highlighter?: Highlighter,
) => MapPlugin = (
  onFeatureHover,
  onFeatureClick,
  hoveredTableRowFeature,
  statusColor,
  highlighter,
) => {
  if (!graphicHighlighter) {
    graphicHighlighter = highlighter ?? GraphicHighlighter();
  }

  return ({
    extendMap: (mapView) => {
      const repeatableLayer = mapView.map.findLayerById(REPEATABLE_LAYER_ID) as GraphicsLayer;
      if (!repeatableLayer) {
        console.error("Couldn't find repeatable layer - maybe this is an error?!?!");
        return;
      }

      const graphicHitTest: GraphicHitTest = GraphicHitTester(mapView, repeatableLayer);

      graphicHitTest.onHover((
        graphic: Graphic | undefined,
      ) => {
        changeCursorOnValidGraphic(mapView, graphic);
        onFeatureHover(graphic?.attributes?.shape?.item);
      });

      graphicHitTest.onClick((
        graphic: Graphic | undefined,
      ) => {
        onFeatureClick(graphic?.attributes?.shape?.item);
      });
    },
    onChange: (mapView) => {
      const currentHoveredRecord = hoveredTableRowFeature();

      graphicHighlighter?.handleRowHover(currentHoveredRecord, mapView, statusColor);
    },
  });
};

export const resetGraphicHighlighter = () => {
  graphicHighlighter = undefined;
};

export default RepeatableInteractivity;
